import { BaseObject } from './BaseObject';
import { SVGRenderer } from '../core/SVGRenderer';
import { Point2D } from '../types';

export class Ruler extends BaseObject {
  public p1: Point2D;
  public p2: Point2D;
  public width: number = 450;
  public height: number = 50;

  private groupElem: SVGGElement | null = null;
  private rectElem: SVGRectElement | null = null;
  private ticksGroupElem: SVGGElement | null = null;

  constructor(
    renderer: SVGRenderer,
    p1: Point2D = { x: 100, y: 300 },
    p2: Point2D = { x: 500, y: 300 },
    id?: string
  ) {
    super(renderer, id);
    this.p1 = { ...p1 };
    this.p2 = { ...p2 };
    this.render();
  }

  public render(): SVGElement {
    this.groupElem = this.renderer.createElement('g', {
      id: this.id,
      class: 'geom-ruler-tool',
      filter: 'drop-shadow(2px 4px 8px rgba(0,0,0,0.15))',
    });

    // Glassmorphic yellow/acrylic translucent ruler background
    this.rectElem = this.renderer.createElement('rect', {
      x: 0,
      y: 0,
      width: this.width,
      height: this.height,
      rx: 4,
      fill: 'rgba(254, 240, 138, 0.75)',
      stroke: '#eab308',
      'stroke-width': 1.5,
    });

    this.ticksGroupElem = this.renderer.createElement('g', {
      fill: '#451a03',
      stroke: '#451a03',
    });

    // Generate metric tick marks along top edge
    this.generateTicks();

    this.groupElem.appendChild(this.rectElem);
    this.groupElem.appendChild(this.ticksGroupElem);

    this.renderer.layers.tools.appendChild(this.groupElem);
    this.element = this.groupElem;
    this.update();
    return this.groupElem;
  }

  private generateTicks(): void {
    if (!this.ticksGroupElem) return;
    this.ticksGroupElem.innerHTML = '';

    const stepPx = 15; // 1cm = 15px scale
    const totalCm = Math.floor(this.width / stepPx);

    for (let i = 0; i <= totalCm; i++) {
      const x = i * stepPx + 20;
      if (x > this.width - 15) break;

      const isMajor = i % 5 === 0;
      const tickH = isMajor ? 16 : 8;

      const line = this.renderer.createElement('line', {
        x1: x,
        y1: 0,
        x2: x,
        y2: tickH,
        stroke: '#78350f',
        'stroke-width': isMajor ? 1.5 : 1.0,
      });
      this.ticksGroupElem.appendChild(line);

      if (isMajor && i > 0) {
        const text = this.renderer.createElement('text', {
          x: x,
          y: tickH + 12,
          fill: '#78350f',
          'font-size': 10,
          'font-weight': 'bold',
          'text-anchor': 'middle',
        });
        text.textContent = String(i / 5);
        this.ticksGroupElem.appendChild(text);
      }
    }
  }

  public setPoints(p1: Point2D, p2: Point2D): void {
    this.p1 = { ...p1 };
    this.p2 = { ...p2 };
    this.update();
  }

  public update(): void {
    if (!this.groupElem) return;

    const angleRad = Math.atan2(this.p2.y - this.p1.y, this.p2.x - this.p1.x);
    const angleDeg = (angleRad * 180) / Math.PI;

    // Offset ruler so top edge aligns with segment baseline
    const transformStr = `translate(${this.p1.x}, ${this.p1.y}) rotate(${angleDeg})`;
    this.groupElem.setAttribute('transform', transformStr);
    this.groupElem.style.display = this.visible ? 'block' : 'none';
  }
}
